import { Mail, Plus } from "lucide-react";
import { useState, type FormEvent, type JSX } from "react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  Label,
  Separator,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useToast,
} from "@mailflare/ui";
import type { DomainId } from "@mailflare/shared";

import { formatBytes } from "@/lib/utils";
import { useCreateMailbox, useMailboxes } from "@/features/mailboxes/hooks";
import { errorText } from "@/features/auth/components/auth-card";

export function MailboxesSection(): JSX.Element {
  const { toast } = useToast();
  const mailboxes = useMailboxes();
  const create = useCreateMailbox();
  const [localPart, setLocalPart] = useState("");
  const [domainId, setDomainId] = useState("");
  const [displayName, setDisplayName] = useState("");

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    create.mutate(
      { localPart, domainId: domainId as DomainId, displayName, kind: "personal" },
      {
        onSuccess: () => {
          toast({ title: "Mailbox created", variant: "success" });
          setLocalPart("");
          setDisplayName("");
        },
        onError: (error) => {
          toast({
            title: "Could not create mailbox",
            description: errorText(error),
            variant: "destructive",
          });
        },
      },
    );
  };

  const items = mailboxes.data ?? [];

  return (
    <section aria-labelledby="mailboxes-heading">
      <Card>
        <CardHeader>
          <CardTitle>
            <h2 id="mailboxes-heading">Mailboxes</h2>
          </CardTitle>
          <CardDescription>Mailboxes attached to your account.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {mailboxes.isLoading ? (
            <div role="status" aria-label="Loading mailboxes" className="space-y-2">
              <Skeleton className="h-11 w-full" />
              <Skeleton className="h-11 w-full" />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={Mail}
              title="No mailboxes yet"
              description="Create your first mailbox below to start sending and receiving mail."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Address</TableHead>
                  <TableHead>Usage</TableHead>
                  <TableHead className="text-right">Kind</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((mailbox) => (
                  <TableRow key={mailbox.id}>
                    <TableCell className="font-medium">{mailbox.address}</TableCell>
                    <TableCell className="text-muted-foreground text-xs">
                      {formatBytes(mailbox.usedBytes)}
                      {mailbox.quotaBytes !== null ? ` of ${formatBytes(mailbox.quotaBytes)}` : ""}
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge variant="outline">{mailbox.kind}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Separator />

          <form className="grid gap-3 sm:grid-cols-3" onSubmit={handleSubmit}>
            <div className="space-y-1.5">
              <Label htmlFor="mailbox-local">Local part</Label>
              <Input
                id="mailbox-local"
                value={localPart}
                onChange={(event) => setLocalPart(event.target.value)}
                placeholder="you"
                pattern="[a-z0-9](?:[a-z0-9._-]*[a-z0-9])?"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="mailbox-domain">Domain id</Label>
              <Input
                id="mailbox-domain"
                value={domainId}
                onChange={(event) => setDomainId(event.target.value)}
                placeholder="ULID"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="mailbox-name">Display name</Label>
              <Input
                id="mailbox-name"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                required
              />
            </div>
            <Button
              type="submit"
              disabled={create.isPending}
              className="sm:col-span-3 sm:justify-self-start"
            >
              <Plus aria-hidden className="size-4" />
              Create mailbox
            </Button>
          </form>
        </CardContent>
      </Card>
    </section>
  );
}
